import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { FILED_EXTERNALLY } from "constants/cartItemDeletionReasons";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useMarkAsNotRequiredMutation } from "store/apis/filing";
import { TaskDeadlineOverviewItem } from "types/Models/task";
import { BackendError } from "types/utils/error";
import { mixed, object } from "yup";

export const TaskFiledExternally = ({
  isOpen,
  close,
  taskDeadline,
  onSuccess,
}: {
  isOpen: boolean;
  close: () => void;
  taskDeadline: TaskDeadlineOverviewItem;
  onSuccess?: () => void;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const [markAsNotRequired, { isLoading }] = useMarkAsNotRequiredMutation();

  const onSubmit = async (values: { file: File | null }) => {
    try {
      if (
        taskDeadline.product_content_type_id &&
        taskDeadline.base_task_template.uuid
      ) {
        const cartItemId = taskDeadline.cart_item_uuid
          ? { cart_item_id: taskDeadline.cart_item_uuid }
          : {};
        const stateId = taskDeadline.alarm_state_id
          ? { state_id: taskDeadline.alarm_state_id }
          : {};
        await markAsNotRequired({
          entityId: taskDeadline.entity.uuid,
          groupId,
          payload: {
            product_content_type_id: taskDeadline.product_content_type_id,
            season: taskDeadline.season,
            product_id: taskDeadline.base_task_template.uuid,
            delete_reason: FILED_EXTERNALLY,
            payment_amount:
              !taskDeadline.payment_amount ||
              isNaN(Number(taskDeadline.payment_amount))
                ? 0
                : Number(taskDeadline.payment_amount),
            file: values.file,
            ...cartItemId,
            ...stateId,
          },
        }).unwrap();
        successToast({
          title: "Filing updated",
          message: "Filing status has been updated to ‘Filed Externally’",
        });
        onSuccess?.();
      }
    } catch (error) {
      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as {}
      );
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      {isOpen && (
        <FormikForm<{ file: File | null }>
          validationSchema={() =>
            object().shape({
              file: mixed().nullable(),
            })
          }
          validateOnChange
          onSubmit={onSubmit}
          enableReinitialize
          initialValues={{ file: null }}
        >
          {({ submitForm, values, setFieldValue }) => {
            return (
              <>
                <Modal.Content useCustomOverlay>
                  <Modal.Header>
                    <Modal.Title>Update Filing Status</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body>
                    <div className="t-flex t-flex-col t-gap-4">
                      <div className="t-text-body-sm t-text-text-60">
                        We need a copy of the filed form to keep your records up
                        to date and ensures compliance.
                      </div>
                      <FileInput
                        withForm
                        name="file"
                        onDelete={() => setFieldValue("file", null)}
                        onDrop={(files) => {
                          const file = files[0] as File;
                          if (file) {
                            setFieldValue("file", file);
                          }
                        }}
                        file={values.file}
                        label="Upload filed copy"
                      />
                    </div>
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button>Cancel</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      onClick={submitForm}
                      isLoading={isLoading}
                      disabled={isLoading}
                    >
                      Mark as ‘Filed Externally’
                    </Button>
                  </Modal.FooterButtonGroup>
                </Modal.Content>
              </>
            );
          }}
        </FormikForm>
      )}
    </Modal.Root>
  );
};
